import React, { useState, useEffect } from 'react';
import { get } from 'lodash/fp';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Table } from 'antd';
import { views } from 'enums/menu-enums';
import { PAGE_SIZE, EXPAND_ICON_COLUMN_INDEX } from 'enums/table-enums';
import { getOrdersAction, getProductVariantsAction } from 'store/actions/data';
import { getSearchResultsAction } from 'store/actions/search';
import { setTableData, tableItemsLabel } from 'utils/table/tableUtils';
import ExpandedRow from 'components/ExpandedRowContent';
import { ExpandedRowTable } from 'components/ExpandedRowTable';
import CurrentSelectedTab from 'components/common/SelectedTab';
import TablePagination from 'components/common/Pagination';
import { scrollToTop } from 'utils/dom';
import { FONTS } from 'styles/theme/fonts';
import { COLORS } from 'styles/theme/colors';

export const PortalTable = ({ columns, table, tab }) => {
  const dispatch = useDispatch();
  const activeListings = useSelector((state) => state.activeListings);
  const openOrders = useSelector((state) => (state.openOrders[tab] ? state.openOrders[tab] : []));
  const completedOrders = useSelector((state) =>
    state.completedOrders[tab] ? state.completedOrders[tab] : [],
  );
  const searchParam = useSelector((state) => state.search.searchParam);
  const [expandedKeys, setExpandedKeys] = useState([]);

  const handleRow = ({ id }) => {
    return expandedKeys.includes(id) && { className: 'expand-parent' };
  };

  // https://stackoverflow.com/questions/49115462/ant-design-table-expanded-change-css-class-react

  const handleExpand = (expanded, { id, storeProductTemplateId }) => {
    const keys = expandedKeys;
    const newKeys = expanded ? keys.concat(id) : keys.filter((k) => k !== id);
    if (expanded) {
      dispatch(getProductVariantsAction(storeProductTemplateId));
    }
    setExpandedKeys(newKeys);
  };

  const changePage = (page) => {
    if (searchParam === '') {
      dispatch(getOrdersAction(tab, table, page));
    } else {
      dispatch(getSearchResultsAction(searchParam, tab, table, page));
    }
    // Scroll to top when changing page
    scrollToTop();
  };

  useEffect(() => {
    setExpandedKeys([]);
  }, [tab]);

  const tableData = setTableData(table, activeListings, openOrders, completedOrders);
  const tableLabel = tableItemsLabel(tableData.total, PAGE_SIZE, tableData.current, table);
  return (
    <React.Fragment>
      <CurrentSelectedTab
        table={table}
        tab={tab}
        openOrders={get('metadata.totalCount')(openOrders) || 0}
        completedOrders={get('metadata.totalCount')(completedOrders) || 0}
      />
      {searchParam !== '' && tableData.data.length < 1 ? (
        <PortalTable.EmptySearchResults>
          <PortalTable.NoMatch>no matching results</PortalTable.NoMatch>
          <PortalTable.Try>
            Try clicking on another tab, checking your spelling, or different keywords.
          </PortalTable.Try>
        </PortalTable.EmptySearchResults>
      ) : (
        <React.Fragment>
          <TablePagination changePage={changePage} tableData={tableData} tableLabel={tableLabel} />
          <Table
            dataSource={tableData.data}
            columns={columns}
            rowKey={(record) => record.id}
            onExpand={handleExpand}
            onRow={handleRow}
            pagination={false}
            expandedRowKeys={expandedKeys}
            expandedRowRender={(order) =>
              table === views.activeListings ? (
                <ExpandedRowTable
                  order={order}
                  tableData={activeListings.variants[order.storeProductTemplateId] || []}
                />
              ) : (
                <ExpandedRow order={order} />
              )
            }
            expandIconColumnIndex={EXPAND_ICON_COLUMN_INDEX}
            expandIconAsCell={false}
          />
          <TablePagination changePage={changePage} tableData={tableData} tableLabel={tableLabel} />
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

PortalTable.EmptySearchResults = styled.div`
  width: 100%;
  height: 70vh;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  font-family: ${FONTS.default};
`;

PortalTable.Try = styled.div`
  font-size: 14px;
  line-height: 17px;
  color: ${COLORS.secondaryText};
  font-family: ${FONTS.default};
`;

PortalTable.NoMatch = styled.div`
  font-size: 18px;
  line-height: 22px;
  color: ${COLORS.primaryText};
  font-family: ${FONTS.default};
  text-transform: capitalize;
`;

PortalTable.PaginationContainer = styled.div`
  height: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

PortalTable.propTypes = {
  tab: PropTypes.string,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      dataIndex: PropTypes.string,
      key: PropTypes.string,
    }),
  ),
  table: PropTypes.string,
};

export default PortalTable;
